import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch } from '@/shared/api';

/** What somebody wants from us. Ordered by how often it is the reason for writing. */
export const FEEDBACK_KINDS = ['Idea', 'Problem', 'Cooperation', 'Business'] as const;

export const feedbackKindSchema = z.enum(FEEDBACK_KINDS);
export const feedbackStatusSchema = z.enum(['New', 'Answered', 'Dismissed']);

export type FeedbackKind = z.infer<typeof feedbackKindSchema>;
export type FeedbackStatus = z.infer<typeof feedbackStatusSchema>;

const feedbackSchema = z.object({
  id: z.string(),
  kind: feedbackKindSchema,
  status: feedbackStatusSchema,
  message: z.string(),
  contact: z.string().nullish(),
  /** Whether it came from somebody signed in — an anonymous message is not worth less. */
  fromSignedInUser: z.boolean(),
  createdAt: z.string(),
});

export type SiteFeedback = z.infer<typeof feedbackSchema>;

export const feedbackKeys = {
  list: (status: FeedbackStatus) => ['feedback', status] as const,
};

export function useSubmitFeedback() {
  return useMutation({
    mutationFn: (input: { kind: FeedbackKind; message: string; contact?: string }) =>
      apiFetch<unknown>('/api/feedback', { method: 'POST', body: JSON.stringify(input) }),
  });
}

export function useFeedback(status: FeedbackStatus, enabled: boolean) {
  return useQuery({
    queryKey: feedbackKeys.list(status),
    queryFn: async () =>
      z.array(feedbackSchema).parse(await apiFetch<unknown>(`/api/feedback?status=${status}`)),
    enabled,
  });
}

export function useResolveFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: FeedbackStatus }) =>
      apiFetch<void>(`/api/feedback/${id}`, { method: 'PUT', body: JSON.stringify({ status }) }),
    // The row leaves one pile and joins another, so all three are refetched rather than
    // moved between caches by hand.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['feedback'] }),
  });
}
